import { c as _c } from "react/compiler-runtime";
import { Map, MapControls, useMap } from "@repo/ui/components/map";
import { useSetAtom } from "jotai";
import * as maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import layers from "protomaps-themes-base";
import { useEffect, useMemo, useRef } from "react";
import { mapBoundsAtom } from "@/lib/atoms/map.atoms";
import { PlacePinsLayer } from "./PlacePinsLayer";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_CENTER = [15.985, 60.279];
const DEFAULT_ZOOM = 13;
const SW = [10.5, 55.3];
const NE = [24.2, 69.1];
const PMTILES_URL = "pmtiles://https://uploads.fasterforward.se/tiles/sweden.pmtiles";
const CARTO_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

// Register PMTiles protocol once globally
let pmtilesRegistered = false;
function ensurePmtilesProtocol() {
  if (pmtilesRegistered) return;
  maplibregl.addProtocol("pmtiles", new Protocol().tile);
  pmtilesRegistered = true;
}
function buildPmtilesStyle() {
  return {
    version: 8,
    glyphs: "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    sources: {
      protomaps: {
        type: "vector",
        url: PMTILES_URL,
        attribution: '<a href="https://protomaps.com">Protomaps</a> | <a href="https://openstreetmap.org">OSM</a>'
      }
    },
    layers: layers("protomaps", "light", "en")
  };
}
export function MapContainer(t0) {
  const $ = _c(18);
  const {
    places,
    usePmtiles: t1,
    selectedPlaceId,
    onSelectPlace,
    onClearSelection,
    flyToRef
  } = t0;
  const usePmtiles = t1 === undefined ? false : t1;
  if (usePmtiles) {
    ensurePmtilesProtocol();
  }
  let t2;
  if ($[0] !== usePmtiles) {
    t2 = usePmtiles ? buildPmtilesStyle() : CARTO_STYLE;
    $[0] = usePmtiles;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      light: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const styles = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [SW, NE];
    t5 = /*#__PURE__*/_jsx(MapControls, {
      position: "top-right",
      showZoom: true,
      showLocate: true,
      showCompass: true,
      showFullscreen: true
    });
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== onClearSelection || $[7] !== onSelectPlace || $[8] !== places || $[9] !== selectedPlaceId) {
    t6 = /*#__PURE__*/_jsx(PlacePinsLayer, {
      places: places,
      selectedPlaceId: selectedPlaceId,
      onSelectPlace: onSelectPlace,
      onClearSelection: onClearSelection
    });
    $[6] = onClearSelection;
    $[7] = onSelectPlace;
    $[8] = places;
    $[9] = selectedPlaceId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = /*#__PURE__*/_jsx(BoundsTracker, {});
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== flyToRef) {
    t8 = /*#__PURE__*/_jsx(FlyToBinding, {
      flyToRef: flyToRef
    });
    $[12] = flyToRef;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== styles || $[15] !== t6 || $[16] !== t8) {
    t9 = /*#__PURE__*/_jsxs(Map, {
      styles: styles,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      maxBounds: t4,
      children: [t5, t6, t7, t8]
    });
    $[14] = styles;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}

// ── Internal helpers (use map context) ──────────────────────────────────────

function BoundsTracker() {
  const $ = _c(5);
  const {
    map,
    isLoaded
  } = useMap();
  const setMapBounds = useSetAtom(mapBoundsAtom);
  const timer = useRef(null);
  let t0;
  let t1;
  if ($[0] !== isLoaded || $[1] !== map || $[2] !== setMapBounds) {
    t0 = () => {
      if (!isLoaded || !map) {
        return;
      }
      const emit = () => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          const b = map.getBounds();
          setMapBounds({
            minLat: b.getSouth(),
            minLng: b.getWest(),
            maxLat: b.getNorth(),
            maxLng: b.getEast()
          });
        }, 300);
      };
      emit();
      map.on("moveend", emit);
      return () => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        map.off("moveend", emit);
      };
    };
    t1 = [isLoaded, map, setMapBounds];
    $[0] = isLoaded;
    $[1] = map;
    $[2] = setMapBounds;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return null;
}
function FlyToBinding(t0) {
  const $ = _c(4);
  const {
    flyToRef
  } = t0;
  const {
    map
  } = useMap();
  let t1;
  let t2;
  if ($[0] !== flyToRef || $[1] !== map) {
    t1 = () => {
      if (!map || !flyToRef) {
        return;
      }
      flyToRef.current = (lng, lat) => {
        const currentZoom = map.getZoom();
        map.flyTo({
          center: [lng, lat],
          zoom: Math.max(currentZoom, 16),
          duration: 1200
        });
      };
      return () => {
        flyToRef.current = null;
      };
    };
    t2 = [map, flyToRef];
    $[0] = flyToRef;
    $[1] = map;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}